/**
 * @fileoverview Core - Prompt & AI Interaction Tracking
 * @module features/core/track-prompt
 * 
 * @description
 * Track AI prompt interactions, completions, and user feedback
 * for AI-powered features.
 * 
 * @useCases
 * - Track AI prompt submissions
 * - Monitor response quality
 * - Measure user satisfaction
 * - Analyze prompt patterns
 * - Optimize AI features
 * 
 * @metrics
 * - Prompts per user
 * - Response acceptance rate
 * - Average response time
 * - User satisfaction score
 * - Token usage
 * 
 * @chartData
 * - Line chart: Prompts over time
 * - Bar chart: Prompts by category
 * - Histogram: Response time distribution
 * - Gauge: Satisfaction score
 * 
 * @example
 * ```typescript
 * const trackPrompt = new TrackPrompt(webId, endpoint, apiKey);
 * 
 * await trackPrompt.create({
 *   eventName: 'ai_completion',
 *   properties: { 
 *     promptType: 'code_suggestion',
 *     accepted: true 
 *   }
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { TimeSeriesData } from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Base configuration for prompt events.
 */
export interface PromptConfig {
  /** Event name */
  eventName: string;
  /** Event properties */
  properties: Record<string, unknown>;
}

/**
 * AI prompt submission configuration.
 */
export interface PromptSubmissionConfig {
  /** Prompt ID */
  promptId: string;
  /** User ID */
  userId?: string;
  /** Prompt text (may be truncated/hashed for privacy) */
  promptText?: string;
  /** Prompt category/type */
  promptType: string;
  /** Model used */
  model?: string;
  /** Input token count */
  inputTokens?: number;
  /** Session/conversation ID */
  conversationId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * AI response configuration.
 */
export interface PromptResponseConfig {
  /** Prompt ID */
  promptId: string;
  /** Response time (ms) */
  responseTime: number;
  /** Output token count */
  outputTokens?: number;
  /** Whether response completed */
  completed: boolean;
  /** Error if any */
  error?: string;
  /** Response quality (if auto-detected) */
  qualityScore?: number;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * User feedback on AI response.
 */
export interface PromptFeedbackConfig {
  /** Prompt ID */
  promptId: string;
  /** User ID */
  userId?: string;
  /** Feedback type */
  feedbackType: 'accepted' | 'rejected' | 'edited' | 'thumbs_up' | 'thumbs_down';
  /** Rating (1-5) */
  rating?: number;
  /** User comment */
  comment?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Prompt tracking response.
 */
export interface PromptResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    tracked: boolean;
    promptId?: string;
  };
  timestamp: string;
}

/**
 * Prompt analytics data.
 */
export interface PromptAnalyticsData {
  /** Summary metrics */
  summary: {
    totalPrompts: number;
    uniqueUsers: number;
    avgResponseTime: number;
    acceptanceRate: number;
    avgSatisfactionScore: number;
    totalTokensUsed: number;
    errorRate: number;
  };
  /** Usage by prompt type */
  byType: Array<{
    promptType: string;
    count: number;
    acceptanceRate: number;
    avgResponseTime: number;
    avgTokens: number;
  }>;
  /** Usage by model */
  byModel: Array<{
    model: string;
    count: number;
    avgResponseTime: number;
    errorRate: number;
  }>;
  /** Feedback breakdown */
  feedback: {
    accepted: number;
    rejected: number;
    edited: number;
    thumbsUp: number;
    thumbsDown: number;
  };
  /** Trends */
  trends: TimeSeriesData;
}

// ============================================================================
// TRACK PROMPT CLASS
// ============================================================================

/**
 * TrackPrompt - Track AI prompt interactions.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides prompt tracking including:
 * - Prompt submissions
 * - Response tracking
 * - User feedback
 * - Quality metrics
 * - Usage analytics
 * 
 * @example
 * ```typescript
 * const trackPrompt = new TrackPrompt(webId, endpoint, apiKey);
 * 
 * // Track prompt submission
 * await trackPrompt.trackSubmission({
 *   promptId: 'prompt_123',
 *   userId: 'user_456',
 *   promptType: 'code_completion',
 *   model: 'gpt-4',
 *   inputTokens: 150
 * });
 * 
 * // Track response
 * await trackPrompt.trackResponse({
 *   promptId: 'prompt_123',
 *   responseTime: 1200,
 *   outputTokens: 300,
 *   completed: true
 * });
 * 
 * // Track user feedback
 * await trackPrompt.trackFeedback({
 *   promptId: 'prompt_123',
 *   feedbackType: 'accepted',
 *   rating: 5
 * });
 * ```
 */
export class TrackPrompt extends FlowGridTransport {
  /**
   * Creates and tracks a prompt event.
   * 
   * @param config - Prompt configuration
   * @returns Promise resolving to prompt response
   */
  async create(config: PromptConfig): Promise<PromptResponse> {
    return await this.init(config.eventName, {
      ...config.properties,
      type: "prompt",
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a prompt submission.
   * 
   * @param config - Prompt submission configuration
   * @returns Promise resolving to prompt response
   */
  async trackSubmission(config: PromptSubmissionConfig): Promise<PromptResponse> {
    return await this.init("prompt_submitted", {
      type: "prompt",
      subtype: "submission",
      promptId: config.promptId,
      userId: config.userId,
      promptType: config.promptType,
      model: config.model,
      inputTokens: config.inputTokens,
      conversationId: config.conversationId,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a prompt response.
   * 
   * @param config - Prompt response configuration
   * @returns Promise resolving to prompt response
   */
  async trackResponse(config: PromptResponseConfig): Promise<PromptResponse> {
    return await this.init("prompt_response", {
      type: "prompt",
      subtype: "response",
      promptId: config.promptId,
      responseTime: config.responseTime,
      outputTokens: config.outputTokens,
      completed: config.completed,
      error: config.error,
      qualityScore: config.qualityScore,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks user feedback on a prompt response.
   * 
   * @param config - Prompt feedback configuration
   * @returns Promise resolving to prompt response
   */
  async trackFeedback(config: PromptFeedbackConfig): Promise<PromptResponse> {
    return await this.init("prompt_feedback", {
      type: "prompt",
      subtype: "feedback",
      promptId: config.promptId,
      userId: config.userId,
      feedbackType: config.feedbackType,
      rating: config.rating,
      comment: config.comment,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a conversation/multi-turn prompt session.
   * 
   * @param conversationId - Conversation ID
   * @param userId - User ID
   * @param turnCount - Number of turns
   * @param totalDuration - Total conversation duration (ms)
   * @returns Promise resolving to prompt response
   */
  async trackConversation(
    conversationId: string,
    userId: string,
    turnCount: number,
    totalDuration: number
  ): Promise<PromptResponse> {
    return await this.init("prompt_conversation", {
      type: "prompt",
      subtype: "conversation",
      conversationId,
      userId,
      turnCount,
      totalDuration,
      timestamp: new Date().toISOString()
    });
  }

}

export default TrackPrompt;
